import {useCustomApi} from '_customHooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

interface ParsedSlasJWT {
    uido?: string
    sub?: string
    iss?: string
    aud?: string
    exp?: number
    iat?: number
}

interface LoginFlowResponse {
    access_token: string
    refresh_token?: string
    refresh_token_expires_in?: number
    expires_in: number
    token_type: string
    customer_id?: string
    enc_user_id?: string
    id_token?: string
    idp_access_token?: string
    usid?: string
    uido?: string
}

const useLoginFlow = () => {
    const {useLoginFlow} = useCustomApi()
    const {locale, site} = useMultiSite()
    const loginFlow = useLoginFlow(locale.id)

    const getRefreshTokenCookieTTLValue = (overrideValue: number, responseValue: number, defaultValue: number): number => {
        if (overrideValue) {
            return overrideValue
        }
        if (responseValue) {
            return responseValue
        }
        return defaultValue
    }
    const convertSecondsToDate = (seconds: number): Date => {
        const now = new Date()
        now.setSeconds(now.getSeconds() + seconds)
        return now
    }
    const parseSlasJWT = (token: string): ParsedSlasJWT => {
        try {
            const base64Url = token.split('.')[1]
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join(''),
            )
            return JSON.parse(jsonPayload)
        } catch (err) {
            console.error('Error parsing JWT:', err)
            return {}
        }
    }
    const setCookie = (name: string, value: string, expires?: Date) => {
        let cookieStr = `${name}=${value}; path=/;`
        if (expires) {
            cookieStr += `expires=${expires.toUTCString()};`
        }
        document.cookie = cookieStr
    }
    const removeCookie = (name: string) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    }
    const handleTokenResponse = (res: LoginFlowResponse, isGuest: boolean) => {
        const DEFAULT_SLAS_REFRESH_TOKEN_GUEST_TTL = 3600 * 24 * 7 // 7 giorni
        const DEFAULT_SLAS_REFRESH_TOKEN_REGISTERED_TTL = 3600 * 24 * 30 // 30 giorni
        // Sovrascrivi i dati principali del token
        localStorage.setItem('access_token_' + site.id, res.access_token || '')
        localStorage.setItem('customer_id_' + site.id, res.customer_id || '')
        localStorage.setItem('enc_user_id_' + site.id, res.enc_user_id || '')
        localStorage.setItem('expires_in_' + site.id, `${res.expires_in || 0}`)
        localStorage.setItem('id_token_' + site.id, res.id_token || '')
        localStorage.setItem('idp_access_token_' + site.id, res.idp_access_token || '')
        localStorage.setItem('token_type_' + site.id, res.token_type || '')
        localStorage.setItem('usid', res.usid || '')
        localStorage.setItem('customer_type_' + site.id, isGuest ? 'guest' : 'registered')
        // Determina la chiave e il valore del refresh token
        const refreshTokenKey = isGuest ? 'refresh_token_guest' : 'refresh_token_registered'
        const overrideValue = isGuest ? DEFAULT_SLAS_REFRESH_TOKEN_GUEST_TTL : DEFAULT_SLAS_REFRESH_TOKEN_REGISTERED_TTL
        const responseValue = res.refresh_token_expires_in
        const defaultValue = isGuest ? DEFAULT_SLAS_REFRESH_TOKEN_GUEST_TTL : DEFAULT_SLAS_REFRESH_TOKEN_REGISTERED_TTL
        // Calcola il TTL del refresh token
        const refreshTokenTTLValue = getRefreshTokenCookieTTLValue(overrideValue, responseValue ?? 0, defaultValue)
        // Salva il refresh token e la sua scadenza
        if (res.refresh_token) {
            const expiresDate = convertSecondsToDate(refreshTokenTTLValue)
            localStorage.setItem(refreshTokenKey, res.refresh_token)
            localStorage.setItem('refresh_token_expires_in', refreshTokenTTLValue.toString())
            localStorage.setItem(`${refreshTokenKey}_expires`, expiresDate.toISOString())
            removeCookie('cc-nx-g_' + site.id)
            setCookie('cc-nx_' + site.id, res.refresh_token, expiresDate)
        }
        // Salva il valore "uido" se presente nel token
        if (res.access_token) {
            const {uido} = parseSlasJWT(res.access_token)
            if (uido) {
                localStorage.setItem('uido', uido)
            }
        }
    }

    const executeLoginFlow = async (credentials) => {
        try {
            const loginResponse = await loginFlow.mutateAsync({
                phone: credentials.phone,
                password: credentials.password,
                unloggedWishlistID: credentials.unloggedWishlistID,
            })
            handleTokenResponse(loginResponse, false)
            return loginResponse
        } catch (error) {
            console.error('Error during login flow:', error)
            throw error
        }
    }

    return {executeLoginFlow}
}

export {useLoginFlow}
